import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useGetClubQuery,
  useGetClubMembersQuery,
  useRemoveClubMemberMutation,
  type AdminClubMember,
} from '../../redux/clubs/clubs.api';

const SUBSCRIPTION_LABEL: Record<string, string> = {
  free: 'Free',
  monthly: 'Monthly subscription',
  one_time: 'One-time payment',
};

const NEAREST_LABEL: Record<string, string> = {
  now: 'Event happening now',
  this_week: 'Event this week',
  empty: 'No upcoming events',
};

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Action failed';
};

const initial = (firstName?: string, lastName?: string): string =>
  (firstName || lastName || '?').charAt(0).toUpperCase();

const formatDate = (s: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field = ({ label, value }: FieldProps): React.JSX.Element => (
  <div className="user-detail__field">
    <span className="user-detail__field-label">{label}</span>
    <span className="user-detail__field-value">{value || '—'}</span>
  </div>
);

export const AdminClubDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const { data: club, isLoading, error } = useGetClubQuery(id ?? '', {
    skip: !id,
  });
  const { data: members } = useGetClubMembersQuery(id ?? '', { skip: !id });
  const [removeMember, { isLoading: isRemoving }] =
    useRemoveClubMemberMutation();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleRemoveMember = async (m: AdminClubMember): Promise<void> => {
    const name = `${m.firstName} ${m.lastName}`.trim() || m.email;
    if (!window.confirm(`Remove ${name} from this club?`)) return;
    setActionError(null);
    setRemovingId(m.id);
    try {
      await removeMember({ clubId: id ?? '', userId: m.id }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setRemovingId(null);
    }
  };

  if (isLoading) {
    return <div className="admin-users__loading">Loading club…</div>;
  }

  if (error || !club) {
    return (
      <div className="user-detail">
        <Link to="/admin/dashboard/clubs" className="user-detail__back">
          ← Back to clubs
        </Link>
        <div className="admin-users__error">
          {error && 'status' in error && error.status === 404
            ? 'Club not found.'
            : 'Failed to load club.'}
        </div>
      </div>
    );
  }

  const priceLine =
    club.subscriptionType === 'free'
      ? 'Free'
      : club.subscriptionPrice
      ? club.subscriptionType === 'monthly'
        ? `$${club.subscriptionPrice} / month`
        : `$${club.subscriptionPrice}`
      : SUBSCRIPTION_LABEL[club.subscriptionType] || club.subscriptionType;

  return (
    <div className="user-detail">
      <Link to="/admin/dashboard/clubs" className="user-detail__back">
        ← Back to clubs
      </Link>

      <div className="event-detail__hero">
        <div className="event-detail__hero-image">
          {club.imageUrl ? (
            <img src={club.imageUrl} alt="" />
          ) : (
            <div className="event-detail__hero-image-blank" />
          )}
        </div>
        <div className="event-detail__hero-info">
          <h2 className="user-detail__name">{club.name}</h2>
          <div className="user-detail__badges">
            <span className="user-detail__badge user-detail__badge--verified">
              {priceLine}
            </span>
            <span
              className={`event-detail__status event-detail__status--${club.nearestEvents === 'now' ? 'in_progress' : club.nearestEvents === 'this_week' ? 'upcoming' : 'finished'}`}
            >
              {NEAREST_LABEL[club.nearestEvents] || club.nearestEvents}
            </span>
          </div>
          <div className="event-detail__hero-meta">
            <div>
              {club.memberCount}{' '}
              {club.memberCount === 1 ? 'member' : 'members'}
            </div>
            <div>Created {formatDate(club.createdAt)}</div>
            <div
              className="event-detail__hero-id"
              title="Click to copy"
              onClick={() => navigator.clipboard?.writeText(club.id)}
            >
              ID: {club.id}
            </div>
          </div>
        </div>
      </div>

      <div className="user-detail__grid">
        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Description</h3>
          {club.description ? (
            <p className="event-detail__description">{club.description}</p>
          ) : (
            <p className="user-detail__empty">No description.</p>
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Subscription</h3>
          <Field
            label="Type"
            value={SUBSCRIPTION_LABEL[club.subscriptionType]}
          />
          <Field
            label="Price"
            value={
              club.subscriptionPrice ? `$${club.subscriptionPrice}` : 'Free'
            }
          />
          <Field label="Members" value={club.memberCount} />
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Owner</h3>
          {club.owner ? (
            <Link
              className="event-detail__user"
              to={`/admin/dashboard/users/${club.owner.id}`}
            >
              {club.owner.avatarUrl ? (
                <img
                  className="event-detail__user-avatar"
                  src={club.owner.avatarUrl}
                  alt=""
                />
              ) : (
                <div className="event-detail__user-avatar event-detail__user-avatar--blank">
                  {initial(club.owner.firstName, club.owner.lastName)}
                </div>
              )}
              <span className="event-detail__user-name">
                {club.owner.firstName} {club.owner.lastName}
              </span>
            </Link>
          ) : (
            <p className="user-detail__empty">No owner recorded.</p>
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Meta</h3>
          <Field label="Created" value={formatDate(club.createdAt)} />
          <Field label="Updated" value={formatDate(club.updatedAt)} />
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">
            Members{' '}
            {members && (
              <span className="event-detail__reports-count event-detail__reports-count--neutral">
                {members.length}
              </span>
            )}
          </h3>
          {!members && (
            <p className="user-detail__empty">Loading members…</p>
          )}
          {members && members.length === 0 && (
            <p className="user-detail__empty">No members yet.</p>
          )}
          {members && members.length > 0 && (
            <div className="event-detail__participants">
              {members.map((m) => {
                const name =
                  [m.firstName, m.lastName].filter(Boolean).join(' ') ||
                  m.email;
                return (
                  <div
                    key={m.id}
                    className="event-detail__participant"
                  >
                    <Link
                      to={`/admin/dashboard/users/${m.id}`}
                      className="event-detail__user"
                    >
                      {m.avatarUrl ? (
                        <img
                          src={m.avatarUrl}
                          alt=""
                          className="event-detail__user-avatar"
                        />
                      ) : (
                        <div className="event-detail__user-avatar event-detail__user-avatar--blank">
                          {(m.firstName || m.email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="event-detail__user-name">{name}</span>
                    </Link>
                    <div className="event-detail__participant-meta">
                      {m.isOwner ? (
                        <span className="user-detail__badge user-detail__badge--premium">
                          Owner
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="event-detail__remove-btn"
                          onClick={() => handleRemoveMember(m)}
                          disabled={isRemoving && removingId === m.id}
                        >
                          {isRemoving && removingId === m.id
                            ? 'Removing…'
                            : 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {actionError && (
            <div className="admin-users__error" style={{ marginTop: 12 }}>
              {actionError}
            </div>
          )}
        </section>

        {club.videos && club.videos.length > 0 && (
          <section className="user-detail__card user-detail__card--wide">
            <h3 className="user-detail__card-title">Videos</h3>
            <div className="user-detail__socials">
              {club.videos.map((v) => (
                <div key={v.id} className="user-detail__social">
                  <span className="user-detail__social-network">{v.name}</span>
                  <a
                    className="user-detail__link"
                    href={v.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {v.url}
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
